import "react-datepicker/dist/react-datepicker.css"

import React, { useEffect, useState } from "react"
import DatePicker from "react-datepicker"
import { trpc } from "~/lib/trpc"

export function GoogleCalendarExplorer() {
  const [selectedCalendarId, setSelectedCalendarId] = useState<string>("")
  const [startDate, setStartDate] = useState<Date>(new Date(new Date().getFullYear(), new Date().getMonth(), 1))
  const [endDate, setEndDate] = useState<Date>(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0))
  const [searchQuery, setSearchQuery] = useState("")
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list")

  const testConnection = trpc.googleCalendar.testConnection.useQuery()
  const listCalendars = trpc.googleCalendar.listCalendars.useQuery(undefined, {
    enabled: testConnection.data?.connected ?? false,
  })

  const listEvents = trpc.googleCalendar.listEvents.useQuery(
    {
      calendarId: selectedCalendarId,
      timeMin: startDate.toISOString(),
      timeMax: endDate.toISOString(),
    },
    {
      enabled: !!selectedCalendarId && testConnection.data?.connected,
    },
  )

  const searchEvents = trpc.googleCalendar.searchEvents.useQuery(
    {
      calendarId: selectedCalendarId,
      query: searchQuery,
      timeMin: startDate.toISOString(),
      timeMax: endDate.toISOString(),
    },
    {
      enabled: !!selectedCalendarId && !!searchQuery && testConnection.data?.connected,
    },
  )

  // Use search results if searching, otherwise use list results
  const events = searchQuery ? searchEvents.data?.events : listEvents.data?.events

  // Auto-select first calendar if available
  useEffect(() => {
    if (listCalendars.data?.calendars.length && !selectedCalendarId) {
      setSelectedCalendarId(listCalendars.data.calendars[0].id)
    }
  }, [listCalendars.data, selectedCalendarId])

  const formatEventTime = (event: any) => {
    if (event.start?.dateTime) {
      const start = new Date(event.start.dateTime)
      const end = event.end?.dateTime ? new Date(event.end.dateTime) : null

      const timeFormat: Intl.DateTimeFormatOptions = {
        hour: "2-digit",
        minute: "2-digit",
      }

      const dateFormat: Intl.DateTimeFormatOptions = {
        weekday: "short",
        month: "short",
        day: "numeric",
      }

      const startDate = start.toLocaleDateString("en-US", dateFormat)
      const startTime = start.toLocaleTimeString("en-US", timeFormat)
      const endTime = end ? end.toLocaleTimeString("en-US", timeFormat) : ""

      return `${startDate} ${startTime}${endTime ? ` - ${endTime}` : ""}`
    } else if (event.start?.date) {
      // All-day event
      const date = new Date(event.start.date)
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    }
    return "No date"
  }

  const getEventColor = (event: any) => {
    // Google Calendar color IDs mapped to Tailwind colors
    const colorMap: Record<string, string> = {
      "1": "bg-blue-100 text-blue-800 border-blue-200",
      "2": "bg-green-100 text-green-800 border-green-200",
      "3": "bg-purple-100 text-purple-800 border-purple-200",
      "4": "bg-red-100 text-destructive border-red-200",
      "5": "bg-yellow-100 text-yellow-800 border-yellow-200",
      "6": "bg-orange-100 text-orange-800 border-orange-200",
      "7": "bg-cyan-100 text-cyan-800 border-cyan-200",
      "8": "bg-foregroundx text-gray-800 border-border",
      "9": "bg-indigo-100 text-indigo-800 border-indigo-200",
      "10": "bg-pink-100 text-pink-800 border-pink-200",
      "11": "bg-red-100 text-destructive border-red-200",
    }

    return colorMap[event.colorId] || "bg-foregroundx text-gray-800 border-border"
  }

  const renderCalendarView = () => {
    if (!events || events.length === 0) return null

    // Group events by date
    const eventsByDate = events.reduce((acc: Record<string, any[]>, event: any) => {
      const dateKey = event.start?.date || event.start?.dateTime?.split("T")[0]
      if (!dateKey) return acc

      if (!acc[dateKey]) acc[dateKey] = []
      acc[dateKey].push(event)
      return acc
    }, {})

    // Generate calendar grid
    const firstDay = new Date(startDate.getFullYear(), startDate.getMonth(), 1)
    const lastDay = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0)
    const startPadding = firstDay.getDay()
    const totalDays = lastDay.getDate()
    const totalCells = Math.ceil((startPadding + totalDays) / 7) * 7

    const calendarDays = []
    for (let i = 0; i < totalCells; i++) {
      const dayNumber = i - startPadding + 1
      const isValidDay = dayNumber > 0 && dayNumber <= totalDays
      const date = isValidDay ? new Date(startDate.getFullYear(), startDate.getMonth(), dayNumber) : null

      const dateKey = date ? date.toISOString().split("T")[0] : ""
      const dayEvents = eventsByDate[dateKey] || []

      calendarDays.push(
        <div key={i} className={`border p-2 min-h-[100px] ${isValidDay ? "bg-background" : "bg-foreground"}`}>
          {isValidDay && (
            <>
              <div className="font-medium text-sm mb-1">{dayNumber}</div>
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((event: any, idx: number) => (
                  <div key={idx} className={`text-xs p-1 rounded border ${getEventColor(event)}`} title={event.summary}>
                    <div className="truncate">{event.summary}</div>
                    {event.start?.dateTime && (
                      <div className="text-[10px] opacity-75">
                        {new Date(event.start.dateTime).toLocaleTimeString("en-US", {
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </div>
                    )}
                  </div>
                ))}
                {dayEvents.length > 3 && <div className="text-xs text-gray-500">+{dayEvents.length - 3} more</div>}
              </div>
            </>
          )}
        </div>,
      )
    }

    return (
      <div>
        <div className="grid grid-cols-7 gap-0 mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} className="text-center font-medium text-sm py-2">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0 border-t border-l">{calendarDays}</div>
      </div>
    )
  }

  return (
    <div className="space-y-4 py-4">
      {/* Connection Status */}
      <div className="bg-foregroundx p-4 rounded-lg">
        <h3 className="font-medium mb-2">Connection Status</h3>
        {testConnection.isLoading ? (
          <p className="">Checking connection...</p>
        ) : testConnection.data ? (
          <div>
            <p className="text-sm">
              <span className="font-medium">Status:</span>{" "}
              <span className={testConnection.data.connected ? "text-green-600" : "text-destructive"}>
                {testConnection.data.connected ? "Connected" : "Not Connected"}
              </span>
            </p>
            <p className="text-sm">
              <span className="font-medium">Service Account:</span> {testConnection.data.serviceAccount}
            </p>
            <p className="text-sm">
              <span className="font-medium">Private Key:</span>{" "}
              <span className={testConnection.data.hasPrivateKey ? "text-green-600" : "text-destructive"}>
                {testConnection.data.hasPrivateKey ? "Configured" : "Not Configured"}
              </span>
            </p>
          </div>
        ) : (
          <p className="text-destructive">Failed to check connection</p>
        )}
      </div>

      {testConnection.data?.connected && (
        <>
          {/* Calendar Selection */}
          <div>
            <label htmlFor="calendar-select" className="block text-sm font-medium text-primary mb-2">
              Select Calendar
            </label>
            {listCalendars.isLoading ? (
              <p className="">Loading calendars...</p>
            ) : listCalendars.data?.calendars.length ? (
              <select
                id="calendar-select"
                value={selectedCalendarId}
                onChange={(e) => setSelectedCalendarId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              >
                {listCalendars.data.calendars.map((calendar: any) => (
                  <option key={calendar.id} value={calendar.id}>
                    {calendar.summary} {calendar.primary && "(Primary)"}
                  </option>
                ))}
              </select>
            ) : (
              <p className="">No calendars found</p>
            )}
          </div>

          {/* Date Range Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Start Date</label>
              <DatePicker
                selected={startDate}
                onChange={(date: Date | null) => date && setStartDate(date)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                dateFormat="MMM d, yyyy"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-2">End Date</label>
              <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => date && setEndDate(date)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                dateFormat="MMM d, yyyy"
              />
            </div>
          </div>

          {/* Search Bar */}
          <div>
            <label className="block text-sm font-medium text-primary mb-2">Search Events</label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by title, description, location..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={() => {
                  if (searchQuery) void searchEvents.refetch()
                  else void listEvents.refetch()
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {searchQuery ? "Search" : "Refresh"}
              </button>
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode("list")}
              className={`px-4 py-2 rounded-md ${
                viewMode === "list" ? "bg-blue-600 text-white" : "bg-foregroundx text-primary hover:bg-gray-300"
              }`}
            >
              List View
            </button>
            <button
              onClick={() => setViewMode("calendar")}
              className={`px-4 py-2 rounded-md ${
                viewMode === "calendar" ? "bg-blue-600 text-white" : "bg-foregroundx text-primary hover:bg-gray-300"
              }`}
            >
              Calendar View
            </button>
          </div>

          {/* Events Display */}
          {listEvents.isLoading || searchEvents.isLoading ? (
            <div className="text-center py-8">
              <p className="">Loading events...</p>
            </div>
          ) : events && events.length > 0 ? (
            viewMode === "list" ? (
              <div className="space-y-2">
                <p className="text-sm  mb-2">
                  Found {events.length} events
                  {searchQuery && ` matching "${searchQuery}"`}
                </p>
                {events.map((event: any, index: number) => (
                  <div key={event.id || index} className={`border rounded-lg p-4 ${getEventColor(event)}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium">{event.summary || "Untitled Event"}</h4>
                        <p className="text-sm mt-1">{formatEventTime(event)}</p>
                        {event.location && (
                          <p className="text-sm mt-1">
                            <span className="font-medium">Location:</span> {event.location}
                          </p>
                        )}
                        {event.description && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-sm font-medium">Description</summary>
                            <p className="text-sm mt-1 whitespace-pre-wrap">{event.description}</p>
                          </details>
                        )}
                        {event.attendees && event.attendees.length > 0 && (
                          <div className="mt-2">
                            <span className="text-sm font-medium">Attendees:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {event.attendees.map((attendee: any, idx: number) => (
                                <span key={idx} className="text-xs px-2 py-1 bg-background rounded">
                                  {attendee.email}
                                  {attendee.organizer && " (Organizer)"}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      {event.htmlLink && (
                        <a
                          href={event.htmlLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-4 text-sm  hover:underline"
                        >
                          Open in Calendar
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              renderCalendarView()
            )
          ) : (
            <div className="text-center py-8 bg-foregroundx rounded-lg">
              <p className="">
                {searchQuery
                  ? `No events found matching "${searchQuery}"`
                  : "No events found in the selected date range"}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
